import { useEffect, useRef, useState } from "react";
import { ArrowDown, ArrowLeft, CheckCircle2, CreditCard, Download, Eraser, FileSignature, Loader2, Printer, Send, ShieldCheck } from "lucide-react";
import { Link } from "wouter";
import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";

type AuthorizationForm = {
  businessName: string;
  legalBusinessName: string;
  legalEntitySelection: string;
  businessOwnerLegalName: string;
  businessOwnerPhone: string;
  businessOwnerEmail: string;
  contactName: string;
  contactTitle: string;
  contactEmail: string;
  contactPhone: string;
  restaurantAddress: string;
  website: string;
  currentDeliverySetup: string;
  squareAccountOwner: string;
  squareLoginEmail: string;
  squareConnectionStatus: string;
  paymentProcessor: string;
  menuSourceOfTruth: string;
  orderNotificationPreference: string;
  launchDeadline: string;
  decisionMaker: string;
  doordashAccountOwner: string;
  doordashDeveloperAccountEmail: string;
  doordashAccountTransferPlan: string;
  doordashLoginEmail: string;
  currentDoorDashMerchantStatus: string;
  pickupInstructions: string;
  dasherPickupPhone: string;
  dasherParkingInstructions: string;
  deliveryRadius: string;
  deliveryHours: string;
  deliveryMinimumOrder: string;
  prepTime: string;
  feePreference: string;
  deliveryFeePolicy: string;
  deliverySubsidyRule: string;
  trackingNotificationPreference: string;
  deliverySupportPhone: string;
  scheduledDeliveryPreference: string;
  testOrderPreference: string;
  testDeliveryAddress: string;
  testDeliveryTiming: string;
  testOrderItem: string;
  billingApproval: string;
  notes: string;
  authorizedName: string;
  authorizedDate: string;
  authorizeDriveAccess: boolean;
  authorizeSquareAccess: boolean;
  authorizeProductionSetup: boolean;
  authorizeLiveTest: boolean;
  authorizeBsaPayment: boolean;
  authorizeQuestionnaireAccuracy: boolean;
};

const today = new Date().toISOString().slice(0, 10);
const squareReturnUrl = "https://www.bettersystems.ai/client-forms/testal-doordash#questionnaire";
const squareConnectUrl = `https://testal-app.vercel.app/api/connect/square/start?next=${encodeURIComponent(squareReturnUrl)}`;

const initialForm: AuthorizationForm = {
  businessName: "Testal Mexican Kitchen",
  legalBusinessName: "Please confirm exact legal entity before DoorDash production request.",
  legalEntitySelection: "Please choose/confirm: Morena Mia Panaderia & Coffee LLC, Testal Tortilleria LLC, Cooltura LLC, or other.",
  businessOwnerLegalName: "",
  businessOwnerPhone: "",
  businessOwnerEmail: "",
  contactName: "Fernando Hernandez",
  contactTitle: "Owner",
  contactEmail: "fernando@testalphx.com",
  contactPhone: "(602) 384-9993",
  restaurantAddress: "1325 Grand Ave, Suite 1, Phoenix, AZ 85007",
  website: "testalphx.com",
  currentDeliverySetup: "Owner.com is being cancelled. Current setup uses DoorDash delivery connected through the existing ordering flow. Owner.com renewal/cutover pressure is around June 27.",
  squareAccountOwner: "Testal / Fernando",
  squareLoginEmail: "Please confirm Square login email. Do not enter password.",
  squareConnectionStatus: "Square account exists and is being connected for menu/orders/payments. Please confirm if DoorDash orders currently appear in Square.",
  paymentProcessor: "Square for online payments, Apple Pay/Google Pay/card when production keys and domain are approved.",
  menuSourceOfTruth: "Use the current Testal online menu as the source of truth, then confirm any final price/photo/item changes before launch.",
  orderNotificationPreference: "Please confirm where orders should be watched first: Square dashboard, Testal Ops/iPad, email, SMS, or another device.",
  launchDeadline: "Owner.com cancellation/renewal pressure is around June 27. Goal is to finish setup and testing before cancellation.",
  decisionMaker: "Fernando Hernandez",
  doordashAccountOwner: "Start with developer@bettersystems.ai if needed for speed, then tie production access to Testal legal entity before go-live.",
  doordashDeveloperAccountEmail: "developer@bettersystems.ai",
  doordashAccountTransferPlan: "BSA may create/start the DoorDash Developer setup, but production access and billing should be approved under Testal's legal business entity.",
  doordashLoginEmail: "",
  currentDoorDashMerchantStatus: "Please confirm: existing DoorDash Marketplace account, DoorDash Drive through Owner.com, both, or not sure.",
  pickupInstructions: "Please confirm: Dasher should pick up inside Testal at the counter unless you want a different pickup instruction.",
  dasherPickupPhone: "(602) 384-9993",
  dasherParkingInstructions: "Please confirm parking/loading instructions for Dashers.",
  deliveryRadius: "Please confirm preferred delivery radius. Recommended starting point: 3-5 miles until tested.",
  deliveryHours: "Please confirm if delivery should follow normal store hours or shorter delivery-only hours.",
  deliveryMinimumOrder: "Please confirm minimum delivery order. Recommended starting point: $20 minimum.",
  prepTime: "Normal prep time is currently 15 minutes. Busy mode can extend to 20-30 minutes.",
  feePreference: "Use DoorDash Drive quoted fee unless BSA and Testal agree to a different customer pricing rule.",
  deliveryFeePolicy: "Recommended launch policy: customer pays the full DoorDash quoted delivery fee.",
  deliverySubsidyRule: "Optional later: free or subsidized delivery over a minimum order amount after we see real fee data.",
  trackingNotificationPreference: "Send DoorDash tracking link by SMS and email when available.",
  deliverySupportPhone: "(602) 384-9993",
  scheduledDeliveryPreference: "ASAP delivery only for launch; scheduled delivery can be added later.",
  testOrderPreference: "Run one real delivery test before public launch.",
  testDeliveryAddress: "Please provide a nearby address for the first real delivery test.",
  testDeliveryTiming: "Please provide best date/time to run one real delivery test.",
  testOrderItem: "Please suggest a simple low-risk item for the delivery test.",
  billingApproval: "BSA may pay required setup/test charges first and invoice Testal for reimbursement.",
  notes: "Please add anything BSA should know before requesting/configuring DoorDash Drive production access.",
  authorizedName: "Fernando Hernandez",
  authorizedDate: today,
  authorizeDriveAccess: false,
  authorizeSquareAccess: false,
  authorizeProductionSetup: false,
  authorizeLiveTest: false,
  authorizeBsaPayment: false,
  authorizeQuestionnaireAccuracy: false,
};

function Field({ label, children, required }: { label: string; children: React.ReactNode; required?: boolean }) {
  return (
    <div className="space-y-2">
      <Label className="text-sm font-extrabold text-[#102033]">
        {label}
        {required ? <span className="text-[#F26B1F]"> *</span> : null}
      </Label>
      {children}
    </div>
  );
}

function Section({ title, eyebrow, children }: { title: string; eyebrow: string; children: React.ReactNode }) {
  return (
    <section className="rounded-lg border border-[#102033]/12 bg-white p-5 shadow-[0_18px_50px_rgba(16,32,51,0.07)] md:p-7">
      <p className="mb-2 text-xs font-extrabold uppercase text-[#F26B1F]">{eyebrow}</p>
      <h2 className="mb-5 text-2xl font-black text-[#102033]" style={{ fontFamily: "'Archivo', sans-serif" }}>
        {title}
      </h2>
      {children}
    </section>
  );
}

export default function TestalDoorDashAuthorizationPage() {
  const { toast } = useToast();
  const [form, setForm] = useState<AuthorizationForm>(initialForm);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const ratio = window.devicePixelRatio || 1;
    canvas.width = Math.floor(rect.width * ratio);
    canvas.height = Math.floor(rect.height * ratio);
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.scale(ratio, ratio);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.lineWidth = 2.4;
    ctx.strokeStyle = "#102033";
  }, []);

  const update = (field: keyof AuthorizationForm, value: string | boolean) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const pointerPosition = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  };

  const startDrawing = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!ctx) return;
    const position = pointerPosition(event);
    ctx.beginPath();
    ctx.moveTo(position.x, position.y);
    setIsDrawing(true);
  };

  const draw = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;
    const ctx = canvasRef.current?.getContext("2d");
    if (!ctx) return;
    const position = pointerPosition(event);
    ctx.lineTo(position.x, position.y);
    ctx.stroke();
    setHasSignature(true);
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  };

  const requiredComplete =
    form.businessName.trim() &&
    form.legalBusinessName.trim() &&
    form.businessOwnerLegalName.trim() &&
    form.businessOwnerPhone.trim() &&
    form.businessOwnerEmail.trim() &&
    form.contactName.trim() &&
    form.contactEmail.trim() &&
    form.restaurantAddress.trim() &&
    form.authorizedName.trim() &&
    form.authorizedDate.trim() &&
    form.authorizeDriveAccess &&
    form.authorizeSquareAccess &&
    form.authorizeProductionSetup &&
    form.authorizeLiveTest &&
    form.authorizeBsaPayment &&
    form.authorizeQuestionnaireAccuracy &&
    hasSignature;

  const signedPayload = () => ({
    ...form,
    signatureImage: canvasRef.current?.toDataURL("image/png") || "",
    submittedFrom: window.location.href,
    testMode: new URLSearchParams(window.location.search).get("testMode") === "1",
  });

  const downloadCopy = () => {
    const blob = new Blob([JSON.stringify(signedPayload(), null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "testal-doordash-drive-authorization.json";
    link.click();
    URL.revokeObjectURL(url);
  };

  const submit = async () => {
    if (!requiredComplete) {
      toast({
        title: "Missing required items",
        description: "Complete the required fields, approval boxes, and signature before submitting.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch("/api/testal-doordash-authorization", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(signedPayload()),
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.message || "Submission failed");
      setIsSubmitted(true);
      toast({ title: "Submitted", description: "BSA received the signed authorization." });
    } catch (error) {
      toast({
        title: "Could not submit",
        description: "Use Download copy or Print/Save PDF as backup, then send it to BSA.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-[#F7F4EE] px-4 py-24 text-[#102033]" style={{ fontFamily: "'Manrope', sans-serif" }}>
        <SEO title="Authorization Submitted" description="Testal setup authorization submitted to Better Systems AI." />
        <div className="mx-auto max-w-2xl rounded-lg border border-[#102033]/12 bg-white p-8 text-center shadow-[0_24px_80px_rgba(16,32,51,0.10)]">
          <CheckCircle2 className="mx-auto mb-5 h-16 w-16 text-[#2F9E6D]" />
              <h1 className="mb-3 text-3xl font-black" style={{ fontFamily: "'Archivo', sans-serif" }}>
                Authorization received
              </h1>
              <p className="mb-6 text-[#536276]">
                BSA has the signed Square/DoorDash authorization and setup questionnaire. We will use this to finish production setup and testing.
              </p>
          <div className="flex flex-col justify-center gap-3 sm:flex-row">
            <Button type="button" onClick={() => window.print()} className="bg-[#102033] hover:bg-[#1D314A]">
              <Printer className="mr-2 h-4 w-4" />
              Print copy
            </Button>
            <Button type="button" variant="outline" onClick={downloadCopy}>
              <Download className="mr-2 h-4 w-4" />
              Download copy
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F4EE] text-[#102033]">
      <SEO
        title="Testal Setup Authorization"
        description="Secure Better Systems AI authorization packet for Testal Square, DoorDash Drive, payments, and launch setup."
        image="https://bettersystems.ai/testal-doordash-authorization-og.png"
        url="https://bettersystems.ai/client-forms/testal-doordash"
        robots="noindex, nofollow"
      />

      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link
        href="https://fonts.googleapis.com/css2?family=Archivo:wght@600;700;800;900&family=Manrope:wght@400;500;600;700;800&display=swap"
        rel="stylesheet"
      />

      <main className="px-4 pb-20 pt-20 md:pt-24" style={{ fontFamily: "'Manrope', sans-serif" }}>
        <div className="mx-auto max-w-6xl">
          <Link href="/client-forms" className="mb-5 inline-flex items-center text-sm font-extrabold uppercase text-[#F26B1F]">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Client forms
          </Link>

          <header className="mb-8 rounded-lg border border-[#102033]/12 bg-[#FFFDF8] p-5 shadow-[0_18px_50px_rgba(16,32,51,0.08)] md:p-8">
            <div className="mb-7 flex flex-col justify-between gap-5 md:flex-row md:items-start">
              <img src="/official-logo.png" alt="Better Systems AI" className="h-12 w-auto object-contain" />
              <div className="rounded-lg border border-[#102033]/12 bg-white px-4 py-3 text-sm text-[#536276]">
                <div className="font-extrabold text-[#102033]">Prepared for</div>
                Testal Mexican Kitchen
              </div>
            </div>

            <div className="max-w-4xl">
              <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-[#102033]/15 bg-white px-3 py-2 text-xs font-extrabold uppercase text-[#102033]/70">
                <FileSignature className="h-4 w-4 text-[#F26B1F]" />
                Square + DoorDash authorization
              </div>
              <h1 className="mb-4 text-4xl font-black leading-[1.05] text-[#102033] md:text-6xl" style={{ fontFamily: "'Archivo', sans-serif", letterSpacing: 0 }}>
                DoorDash delivery setup
              </h1>
              <p className="max-w-3xl text-lg leading-8 text-[#475569]">
                Please confirm the few details DoorDash needs so BSA can request production access, finish setup, and run one live delivery test.
              </p>
              {new URLSearchParams(window.location.search).get("testMode") === "1" ? (
                <div className="mt-5 rounded-lg border border-[#2F9E6D]/30 bg-[#EAF7F0] p-4 text-sm font-bold leading-6 text-[#176946]">
                  Internal test mode is active. This saves to BSA as a test record and only sends TEST email to BSA.
                </div>
              ) : null}
              <div className="mt-5 rounded-lg border border-[#F26B1F]/25 bg-[#FFF7ED] p-4 text-sm leading-6 text-[#7C3F16]">
                No passwords are collected here. BSA may start the developer setup with our developer email, but production delivery should be approved under Testal's legal business information.
              </div>
            </div>
          </header>

          <section className="mb-8 rounded-lg border border-[#102033]/12 bg-white p-5 shadow-[0_18px_50px_rgba(16,32,51,0.07)] md:p-7">
            <div className="grid gap-5 lg:grid-cols-[1fr_auto] lg:items-center">
              <div>
                <p className="mb-2 text-xs font-extrabold uppercase text-[#F26B1F]">Step 1</p>
                <h2 className="mb-3 text-2xl font-black text-[#102033]" style={{ fontFamily: "'Archivo', sans-serif" }}>
                  Fast setup path
                </h2>
                <p className="max-w-3xl text-base leading-7 text-[#536276]">
                  BSA can start the DoorDash developer setup with <strong>developer@bettersystems.ai</strong> so we do not lose time. Before go-live, the production request, business approval, and billing should be tied back to Testal's legal business entity.
                </p>
                <div className="mt-4 grid gap-3 text-sm font-bold text-[#102033] md:grid-cols-3">
                  <div className="rounded-lg border border-[#102033]/10 bg-[#FFFDF8] p-3">BSA starts developer setup</div>
                  <div className="rounded-lg border border-[#102033]/10 bg-[#FFFDF8] p-3">Testal approves production info</div>
                  <div className="rounded-lg border border-[#102033]/10 bg-[#FFFDF8] p-3">One real delivery test before launch</div>
                </div>
              </div>
              <div className="flex flex-col gap-3">
                <a
                  href="#questionnaire"
                  className="inline-flex h-14 items-center justify-center rounded-lg bg-[#102033] px-6 text-base font-black text-white transition hover:bg-[#1D314A]"
                >
                  <ArrowDown className="mr-2 h-4 w-4" />
                  Start questionnaire
                </a>
              </div>
            </div>
          </section>

          <div id="questionnaire" className="grid scroll-mt-24 gap-5 lg:grid-cols-[1fr_340px]">
            <div className="space-y-5">
              <Section eyebrow="Step 2" title="1. Legal business details">
                <div className="grid gap-4 md:grid-cols-2">
                  <Field label="Restaurant name" required>
                    <Input value={form.businessName} onChange={(e) => update("businessName", e.target.value)} />
                  </Field>
                  <Field label="Legal business name / entity" required>
                    <Input value={form.legalBusinessName} onChange={(e) => update("legalBusinessName", e.target.value)} placeholder="Fernando: confirm exact legal entity if different from Testal Mexican Kitchen" />
                  </Field>
                  <Field label="Legal entity to use for DoorDash">
                    <Textarea rows={3} value={form.legalEntitySelection} onChange={(e) => update("legalEntitySelection", e.target.value)} />
                  </Field>
                  <Field label="Business owner full legal name" required>
                    <Input value={form.businessOwnerLegalName} onChange={(e) => update("businessOwnerLegalName", e.target.value)} placeholder="Required for DoorDash production approval" />
                  </Field>
                  <Field label="Business owner phone" required>
                    <Input value={form.businessOwnerPhone} onChange={(e) => update("businessOwnerPhone", e.target.value)} placeholder="Required for DoorDash production approval" />
                  </Field>
                  <Field label="Business owner email" required>
                    <Input type="email" value={form.businessOwnerEmail} onChange={(e) => update("businessOwnerEmail", e.target.value)} placeholder="Required for DoorDash production approval" />
                  </Field>
                  <Field label="Primary contact">
                    <Input value={form.contactName} onChange={(e) => update("contactName", e.target.value)} />
                  </Field>
                  <Field label="Contact email">
                    <Input type="email" value={form.contactEmail} onChange={(e) => update("contactEmail", e.target.value)} />
                  </Field>
                  <Field label="Restaurant phone">
                    <Input value={form.contactPhone} onChange={(e) => update("contactPhone", e.target.value)} />
                  </Field>
                  <Field label="Pickup address" required>
                    <Input value={form.restaurantAddress} onChange={(e) => update("restaurantAddress", e.target.value)} />
                  </Field>
                </div>
              </Section>

              <Section eyebrow="Step 3" title="2. Existing DoorDash status">
                <div className="grid gap-4 md:grid-cols-2">
                  <Field label="Current DoorDash status">
                    <Textarea rows={4} value={form.currentDoorDashMerchantStatus} onChange={(e) => update("currentDoorDashMerchantStatus", e.target.value)} />
                  </Field>
                  <Field label="DoorDash account email, if any">
                    <Input value={form.doordashLoginEmail} onChange={(e) => update("doordashLoginEmail", e.target.value)} placeholder="Email only. No password." />
                  </Field>
                  <Field label="BSA developer setup email">
                    <Input value={form.doordashDeveloperAccountEmail} onChange={(e) => update("doordashDeveloperAccountEmail", e.target.value)} />
                  </Field>
                  <Field label="Ownership plan">
                    <Textarea rows={3} value={form.doordashAccountTransferPlan} onChange={(e) => update("doordashAccountTransferPlan", e.target.value)} />
                  </Field>
                </div>
              </Section>

              <Section eyebrow="Step 4" title="3. Delivery rules">
                <div className="grid gap-4 md:grid-cols-2">
                  <Field label="Delivery radius">
                    <Input value={form.deliveryRadius} onChange={(e) => update("deliveryRadius", e.target.value)} placeholder="Example: 5 miles" />
                  </Field>
                  <Field label="Delivery hours">
                    <Input value={form.deliveryHours} onChange={(e) => update("deliveryHours", e.target.value)} placeholder="Example: same as store hours" />
                  </Field>
                  <Field label="Minimum delivery order">
                    <Input value={form.deliveryMinimumOrder} onChange={(e) => update("deliveryMinimumOrder", e.target.value)} placeholder="Example: $20 minimum" />
                  </Field>
                  <Field label="Delivery fee policy">
                    <Textarea rows={3} value={form.deliveryFeePolicy} onChange={(e) => update("deliveryFeePolicy", e.target.value)} />
                  </Field>
                  <Field label="Pickup issue phone for DoorDash">
                    <Input value={form.dasherPickupPhone} onChange={(e) => update("dasherPickupPhone", e.target.value)} />
                  </Field>
                  <Field label="Customer delivery support phone">
                    <Input value={form.deliverySupportPhone} onChange={(e) => update("deliverySupportPhone", e.target.value)} />
                  </Field>
                  <div className="md:col-span-2">
                    <Field label="Dasher pickup instructions">
                      <Textarea rows={4} value={form.pickupInstructions} onChange={(e) => update("pickupInstructions", e.target.value)} placeholder="Where should Dashers park, enter, and pick up orders?" />
                    </Field>
                  </div>
                  <div className="md:col-span-2">
                    <Field label="Dasher parking / loading instructions">
                      <Textarea rows={3} value={form.dasherParkingInstructions} onChange={(e) => update("dasherParkingInstructions", e.target.value)} />
                    </Field>
                  </div>
                </div>
              </Section>

              <Section eyebrow="Step 5" title="4. Test order and billing approval">
                <div className="grid gap-4 md:grid-cols-2">
                  <Field label="Live test preference">
                    <Textarea rows={3} value={form.testOrderPreference} onChange={(e) => update("testOrderPreference", e.target.value)} />
                  </Field>
                  <Field label="Test delivery address">
                    <Textarea rows={3} value={form.testDeliveryAddress} onChange={(e) => update("testDeliveryAddress", e.target.value)} />
                  </Field>
                  <Field label="Best test delivery date/time">
                    <Input value={form.testDeliveryTiming} onChange={(e) => update("testDeliveryTiming", e.target.value)} />
                  </Field>
                  <Field label="Test order item">
                    <Input value={form.testOrderItem} onChange={(e) => update("testOrderItem", e.target.value)} />
                  </Field>
                  <div className="md:col-span-2">
                    <Field label="Billing approval">
                      <Textarea rows={3} value={form.billingApproval} onChange={(e) => update("billingApproval", e.target.value)} />
                    </Field>
                  </div>
                  <div className="md:col-span-2">
                    <Field label="Anything else BSA should know">
                      <Textarea rows={3} value={form.notes} onChange={(e) => update("notes", e.target.value)} />
                    </Field>
                  </div>
                </div>
              </Section>

              <Section eyebrow="Step 6" title="5. Authorization and signature">
                <div className="space-y-3">
                  {[
                    ["authorizeDriveAccess", "I authorize BSA to request and configure DoorDash Drive production access for Testal."],
                    ["authorizeSquareAccess", "I authorize BSA to connect/configure Square for Testal's online ordering, payments, menu, and order flow."],
                    ["authorizeProductionSetup", "I authorize BSA to connect Square and DoorDash Drive to Testal's online ordering flow."],
                    ["authorizeLiveTest", "I authorize BSA to run one real delivery test before public launch."],
                    ["authorizeBsaPayment", "I authorize BSA to pay required setup/test charges first and invoice Testal for reimbursement."],
                    ["authorizeQuestionnaireAccuracy", "I confirm the information in this questionnaire is accurate or corrected to the best of my knowledge."],
                  ].map(([field, label]) => (
                    <label key={field} className="flex gap-3 rounded-lg border border-[#102033]/12 bg-[#FFFDF8] p-4 text-sm font-bold leading-6 text-[#102033]">
                      <input
                        type="checkbox"
                        className="mt-1 h-5 w-5 shrink-0 accent-[#F26B1F]"
                        checked={Boolean(form[field as keyof AuthorizationForm])}
                        onChange={(e) => update(field as keyof AuthorizationForm, e.target.checked)}
                      />
                      {label}
                    </label>
                  ))}
                </div>

                <div className="mt-5 grid gap-4 md:grid-cols-2">
                  <Field label="Authorized signer name" required>
                    <Input value={form.authorizedName} onChange={(e) => update("authorizedName", e.target.value)} />
                  </Field>
                  <Field label="Date" required>
                    <Input type="date" value={form.authorizedDate} onChange={(e) => update("authorizedDate", e.target.value)} />
                  </Field>
                </div>

                <div className="mt-5">
                  <div className="mb-2 flex items-center justify-between gap-3">
                    <Label className="text-sm font-extrabold text-[#102033]">Signature *</Label>
                    <Button type="button" variant="outline" size="sm" onClick={clearSignature}>
                      <Eraser className="mr-2 h-4 w-4" />
                      Clear
                    </Button>
                  </div>
                  <canvas
                    ref={canvasRef}
                    className="h-44 w-full touch-none rounded-lg border-2 border-dashed border-[#102033]/25 bg-white"
                    onPointerDown={startDrawing}
                    onPointerMove={draw}
                    onPointerUp={stopDrawing}
                    onPointerLeave={stopDrawing}
                  />
                  <p className="mt-2 text-sm text-[#64748B]">Sign above with mouse, trackpad, or finger.</p>
                </div>
              </Section>
            </div>

            <aside className="h-fit rounded-lg border border-[#102033]/12 bg-[#102033] p-5 text-white shadow-[0_24px_80px_rgba(16,32,51,0.18)] lg:sticky lg:top-28">
              <ShieldCheck className="mb-4 h-9 w-9 text-[#F26B1F]" />
              <h2 className="mb-3 text-2xl font-black !text-white" style={{ fontFamily: "'Archivo', sans-serif" }}>
                Before launch
              </h2>
              <ul className="mb-6 space-y-3 text-sm leading-6 text-white/78">
                <li>DoorDash production approval may still require DoorDash review.</li>
                <li>Square production features may require Square account approval, domain approval, and live credentials.</li>
                <li>No passwords should be entered in this form.</li>
                <li>BSA will confirm successful setup before public delivery launch.</li>
                <li>After signing, Fernando receives a confirmation email with a PDF copy attached.</li>
                <li>BSA receives an internal action email with the signed PDF attached.</li>
              </ul>
              <div className="space-y-3">
                <Button type="button" className="h-12 w-full bg-[#F26B1F] font-extrabold hover:bg-[#D95B17]" onClick={submit} disabled={isSubmitting}>
                  {isSubmitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
                  Submit signed form
                </Button>
                <Button type="button" variant="outline" className="h-12 w-full bg-white text-[#102033] hover:bg-[#F7F4EE]" onClick={() => window.print()}>
                  <Printer className="mr-2 h-4 w-4" />
                  Print / Save PDF
                </Button>
                <Button type="button" variant="outline" className="h-12 w-full bg-white text-[#102033] hover:bg-[#F7F4EE]" onClick={downloadCopy}>
                  <Download className="mr-2 h-4 w-4" />
                  Download copy
                </Button>
              </div>
            </aside>
          </div>
        </div>
      </main>
    </div>
  );
}
